/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  forwardRef,
  inject,
  Injector,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { skip } from 'rxjs';
import { MatRipple } from '@angular/material/core';
import { type MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

let nextId = 0;

@Component({
  selector: 'app-input-field',
  imports: [
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatRipple,
    MatChipsModule,
    FormsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputField),
      multi: true,
    },
  ],
  templateUrl: './input-field.html',
  styleUrl: './input-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputField implements OnInit, ControlValueAccessor {
  private injector = inject(Injector);

  private cdr = inject(ChangeDetectorRef);

  icon = input<IconProp>();

  label = input();

  placeholder = input('');

  hint = input('');

  type = input<
    'text' | 'textarea' | 'password' | 'number' | 'select' | 'autocomplete' | 'chips-autocomplete'
  >('text');

  autocomplete = input<string>();

  options = input<{ label: string; value: string }[]>([]);

  loading = input<boolean>(false);

  errorMessages = input<Record<string, string>>();

  prefixText = input<string>();

  suffixText = input<string>();

  searchText = model<string>();

  inputId = `input-${nextId++}`;

  autoControl = new FormControl(); // To be used with the autocomplete

  value: any = null;

  disabled = false;

  touched = false;

  hide = signal(true);

  // For chips
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  readonly filteredOptions = computed(() => {
    const currentOption = this.searchText()?.toLowerCase();
    return currentOption
      ? this.options().filter((option) => option.label.toLowerCase().includes(currentOption))
      : this.options().slice();
  });

  ngOnInit(): void {
    if (this.isAutocomplete)
      this.autoControl.valueChanges.pipe(skip(1)).subscribe((value) => {
        // Because null is also considered an object
        if (typeof value === 'object' && value !== null) {
          this.updateValue(value.value);
        } else {
          if (!value) this.updateValue(value);
          this.searchText.set(value);
        }
      });
  }

  // callbacks provided by Angular forms
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  private onChange = (value: any) => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched = () => {};

  // called by Angular
  writeValue(value: any): void {
    this.value = value;

    if (this.isAutocomplete && (value === null || value === undefined || value === '')) {
      this.autoControl.setValue(null, { emitEvent: false });
    }

    // if (this.isAutocomplete) {
    //   const newValue = this.options().find((o) => o.value === value);
    //   if (newValue) {
    //     this.autoControl.setValue(newValue);
    //   } else {
    //     this.autoControl.setValue(undefined, { emitEvent: false }); // To update(clear) the search value
    //   }
    //   this.autoControl.updateValueAndValidity();
    // }
  }

  // called by Angular
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // called by Angular
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // called by Angular
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.autoControl.disable();
    else this.autoControl.enable();
    this.cdr.markForCheck();
  }

  // internal value update
  updateValue(value: any): void {
    this.value = value;

    this.onChange(value);

    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  handleBlur(): void {
    this.onTouched();
  }

  passwordTrigger(event: MouseEvent): void {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  get isAutocomplete(): boolean {
    return this.type() === 'autocomplete';
  }

  get isSelect(): boolean {
    return this.type() === 'select';
  }

  get isChipsAutocomplete(): boolean {
    return this.type() === 'chips-autocomplete';
  }

  get isTextarea(): boolean {
    return this.type() === 'textarea';
  }

  get ngControl(): NgControl | null {
    return this.injector.get(NgControl, null);
  }

  get control() {
    return this.ngControl?.control;
  }

  get showErrors(): boolean {
    const control = this.control;

    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  get showAutoErrors(): boolean {
    const control = this.autoControl;

    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  get errorMessage(): string | null {
    const errors = this.control?.errors;
    const messages = this.errorMessages();

    if (!errors) return null;

    for (const key of Object.keys(errors)) {
      const message = messages ? messages[key] : errors[key];

      if (message && typeof message === 'string') {
        return message;
      }
    }

    return null;
  }

  get isRequired(): boolean {
    return this.control?.hasValidator(Validators.required) ?? false;
  }

  displayOption(option: { label: string; value: string }): string {
    return option?.label || '';
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.updateValue([...this.value, value]);
    }

    // Clear the input value
    this.searchText.set('');
    event.chipInput.clear();
  }

  remove(option: string): void {
    const index = this.value.indexOf(option);
    if (index < 0) {
      return;
    }
    const options = [...this.value];
    options.splice(index, 1);
    this.updateValue([...options]);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (!this.value.includes(event.option.value))
      this.updateValue([...this.value, event.option.value]);
    this.searchText.set('');
    event.option.deselect();
  }
}
